import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  type IconName,
  LoadingIcon,
  Text,
  Textarea,
  cn,
  useToast,
} from '@eluve/components';
import { UserFileTypesLookup } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { calculateFileMd5 } from '@eluve/utils';

export type CreateTemplateMode = 'ai' | 'upload' | 'manual';

const ONE_MB = 1024 * 1024;
const MAX_FILE_SIZE = 10 * ONE_MB;

type ModeChrome = {
  icon: IconName;
  /** Classes for the icon tile at rest + on card hover */
  tileClassName: string;
};

const MODE_CHROME: Record<CreateTemplateMode, ModeChrome> = {
  ai: {
    icon: 'WandSparkles',
    tileClassName:
      'bg-bg-indigo-subtle-primary text-text-indigo group-hover:bg-bg-indigo-subtle-secondary',
  },
  upload: {
    icon: 'FileUp',
    tileClassName:
      'bg-bg-secondary text-text-secondary group-hover:bg-bg-tertiary',
  },
  manual: {
    icon: 'SquarePen',
    tileClassName:
      'bg-bg-secondary text-text-secondary group-hover:bg-bg-tertiary',
  },
};

// Private Components

const ModeCard: React.FC<{
  mode: CreateTemplateMode;
  onSelect: (mode: CreateTemplateMode) => void;
}> = ({ mode, onSelect }) => {
  const { t } = useTranslation();
  const { icon, tileClassName } = MODE_CHROME[mode];
  const modeText: Record<
    CreateTemplateMode,
    { title: string; description: string }
  > = {
    ai: {
      title: t('outputTemplates.generateWithAi.title'),
      description: t('outputTemplates.wizard.aiModeDescription'),
    },
    upload: {
      title: t('outputTemplates.wizard.uploadModeTitle'),
      description: t('outputTemplates.wizard.uploadModeDescription'),
    },
    manual: {
      title: t('outputTemplates.wizard.manualModeTitle'),
      description: t('outputTemplates.wizard.manualModeDescription'),
    },
  };
  const { title, description } = modeText[mode];

  return (
    <button
      type="button"
      onClick={() => onSelect(mode)}
      className="group flex w-full items-center gap-4 rounded-lg border border-border-primary p-4 text-left transition-colors duration-200 hover:border-border-tertiary hover:bg-bg-secondary/60 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
    >
      <div
        className={`flex size-11 shrink-0 items-center justify-center rounded-lg transition-all duration-200 group-hover:scale-105 ${tileClassName}`}
      >
        <Icon name={icon} size="xl" />
      </div>
      <div className="min-w-0 flex-1">
        <Text weight="semibold">{title}</Text>
        <Text className="block" color="tertiary" size="xs">
          {description}
        </Text>
      </div>
      <Icon
        name="ChevronRight"
        size="lg"
        className="-translate-x-1 text-text-tertiary opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
      />
    </button>
  );
};

const ChooseModeStep: React.FC<{
  onSelect: (mode: CreateTemplateMode) => void;
}> = ({ onSelect }) => {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('outputTemplates.wizard.title')}</DialogTitle>
        <DialogDescription>
          {t('outputTemplates.wizard.subtitle')}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-3">
        <ModeCard mode="ai" onSelect={onSelect} />
        <ModeCard mode="upload" onSelect={onSelect} />
      </div>
      <button
        type="button"
        onClick={() => onSelect('manual')}
        className="mx-auto flex w-fit items-center gap-1.5 rounded-sm text-sm text-text-tertiary underline-offset-4 transition-colors hover:text-text-primary hover:underline focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <Icon name="SquarePen" size="sm" />
        {t('outputTemplates.wizard.orStartFromScratch')}
      </button>
    </>
  );
};

/**
 * "Upload or import an existing note" step: the user drops in a PDF of
 * a past note (or pastes its text) and we reverse-engineer a template
 * from it (Opus behind the scenes), landing them in the authoring
 * workspace with the generated sections pre-filled.
 */
const UploadNoteStep: React.FC<{
  onBack: () => void;
  onGenerated: (outputTemplate: unknown) => void;
}> = ({ onBack, onGenerated }) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [noteText, setNoteText] = useState('');
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'generating'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isBusy = phase !== 'idle';
  const trimmedNote = noteText.trim();
  const canGenerate = Boolean(file) || trimmedNote.length > 0;

  const selectFile = (selected: File) => {
    if (!selected.name.toLowerCase().endsWith('.pdf')) {
      setError(t('outputTemplates.wizard.onlyPdfSupported'));
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError(t('outputTemplates.wizard.fileTooLarge'));
      return;
    }
    setError(null);
    setFile(selected);
  };

  const uploadFile = async (selected: File): Promise<string> => {
    if (!tenantId) {
      throw new Error('Unable to determine tenant');
    }

    const fileHash = await calculateFileMd5(selected);

    const prepareResponse = await apiClient.users.prepareUserFileUpload({
      params: { tenantId },
      body: {
        extension: 'pdf',
        fileType: UserFileTypesLookup.CHAT_DOCUMENT,
        fileName: selected.name,
        fileSize: selected.size,
        fileHash,
      },
    });

    if (prepareResponse.status !== 200) {
      throw new Error(t('outputTemplates.wizard.prepareUploadFailed'));
    }

    const { uploadUrl, userUploadId } = prepareResponse.body;

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: selected,
    });

    if (!uploadResponse.ok) {
      await apiClient.users.markUserFileUploadFailed({
        params: { tenantId },
        body: { userUploadId },
      });
      throw new Error(t('outputTemplates.wizard.uploadFailed'));
    }

    await apiClient.users.markUserFileUploadComplete({
      params: { tenantId },
      body: { userUploadId },
    });

    return userUploadId;
  };

  const handleGenerate = async () => {
    if (!canGenerate || !tenantId || isBusy) {
      return;
    }

    setError(null);

    try {
      let userUploadId: string | undefined;
      if (file) {
        setPhase('uploading');
        userUploadId = await uploadFile(file);
      }

      setPhase('generating');

      const generateResponse =
        await apiClient.llm.generateTemplateBundleFromNote({
          params: { tenantId },
          body: userUploadId ? { userUploadId } : { noteText: trimmedNote },
        });

      if (generateResponse.status !== 201) {
        throw new Error(t('outputTemplates.wizard.failedToGenerateFromNote'));
      }

      if (
        generateResponse.body.status === 'rejected' ||
        !generateResponse.body.outputTemplate
      ) {
        setPhase('idle');
        setError(
          generateResponse.body.reason ??
            t('outputTemplates.wizard.unableToGenerateFromNote'),
        );
        return;
      }

      onGenerated(generateResponse.body.outputTemplate);
    } catch (generateError) {
      setPhase('idle');
      const message =
        generateError instanceof Error
          ? generateError.message
          : t('outputTemplates.common.somethingWentWrong');
      setError(message);
      toast.error(t('outputTemplates.common.generationFailed', { message }));
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('outputTemplates.wizard.back')}
            className="-translate-x-2 text-text-tertiary"
            onClick={onBack}
            disabled={isBusy}
          >
            <Icon name="ChevronLeft" size="md" />
          </Button>
          <DialogTitle className="-translate-x-2">
            {t('outputTemplates.wizard.uploadModeTitle')}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t('outputTemplates.wizard.uploadStepDescription')}
        </DialogDescription>
      </DialogHeader>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={(event) => {
          const selected = event.target.files?.[0];
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          if (selected) {
            selectFile(selected);
          }
        }}
      />

      <button
        type="button"
        disabled={isBusy}
        onClick={() => fileInputRef.current?.click()}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          if (isBusy) {
            return;
          }
          const dropped = event.dataTransfer.files[0];
          if (dropped) {
            selectFile(dropped);
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!isBusy && event.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
          }
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        className={cn(
          'flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-6 transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
          isDragging
            ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary/30'
            : 'border-border-primary hover:border-border-tertiary hover:bg-bg-secondary/60',
          isBusy && 'pointer-events-none opacity-70',
        )}
      >
        {isBusy ? (
          <LoadingIcon size="lg" />
        ) : (
          <div className="flex size-9 items-center justify-center rounded-lg bg-bg-secondary text-text-secondary">
            <Icon name="FileUp" size="lg" />
          </div>
        )}
        {isBusy ? (
          <Text size="sm" color="secondary">
            {phase === 'uploading'
              ? t('outputTemplates.wizard.uploadingFile', {
                  fileName:
                    file?.name ?? t('outputTemplates.wizard.fileFallback'),
                })
              : t('outputTemplates.wizard.analyzingNote')}
          </Text>
        ) : (
          <>
            <Text size="sm" color="secondary">
              {t('outputTemplates.wizard.dropZoneHint')}
            </Text>
            <Text size="xs" color="tertiary">
              {t('outputTemplates.wizard.dropZoneLimit')}
            </Text>
          </>
        )}
      </button>

      {file && !isBusy && (
        <div className="flex items-center gap-2 rounded-lg border border-border-primary px-3 py-2">
          <Icon name="FileText" size="sm" className="text-text-secondary" />
          <Text size="sm" weight="semibold" className="min-w-0 truncate">
            {file.name}
          </Text>
          <Text size="xs" color="tertiary" className="shrink-0">
            {file.size >= ONE_MB
              ? `${(file.size / ONE_MB).toFixed(1)} MB`
              : `${Math.max(1, Math.round(file.size / 1024))} KB`}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('outputTemplates.wizard.removeFile')}
            className="ml-auto text-text-tertiary"
            onClick={() => setFile(null)}
          >
            <Icon name="X" size="sm" />
          </Button>
        </div>
      )}

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-border-primary" />
        <Text size="xs" weight="semibold" color="tertiary">
          {t('outputTemplates.wizard.orDivider')}
        </Text>
        <div className="h-px flex-1 bg-border-primary" />
      </div>

      <div className="flex flex-col gap-1.5">
        <Textarea
          rows={6}
          value={noteText}
          disabled={Boolean(file) || isBusy}
          onChange={(event) => setNoteText(event.target.value)}
          placeholder={t('outputTemplates.wizard.pasteNotePlaceholder')}
          className="resize-none"
        />
        {file && (
          <Text size="xs" color="tertiary">
            {t('outputTemplates.wizard.removeFileToPaste')}
          </Text>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-border-negative-subtle bg-bg-negative-subtle px-3 py-2">
          <Text size="xs" className="text-text-negative">
            {error}
          </Text>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleGenerate} disabled={!canGenerate || isBusy}>
          {isBusy
            ? t('outputTemplates.common.generating')
            : t('outputTemplates.common.generate')}
        </Button>
      </div>
    </>
  );
};

// Public Components

export interface CreateTemplateBundleWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Wizard for creating a new template. The first step asks how the user
 * wants to start: AI-assisted generation, importing an existing note,
 * or building manually from scratch.
 */
export const CreateTemplateBundleWizard: React.FC<
  CreateTemplateBundleWizardProps
> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<CreateTemplateMode | null>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setMode(null);
    }
    onOpenChange(nextOpen);
  };

  const handleModeSelected = (selectedMode: CreateTemplateMode) => {
    if (selectedMode === 'manual') {
      handleOpenChange(false);
      navigate('new');
      return;
    }
    if (selectedMode === 'ai') {
      handleOpenChange(false);
      navigate('new/ai');
      return;
    }
    setMode(selectedMode);
  };

  const handleGenerated = (outputTemplate: unknown) => {
    handleOpenChange(false);
    navigate('new', { state: { generatedTemplate: outputTemplate } });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size={mode === 'upload' ? '2xl' : 'lg'}
        className="gap-5 p-6"
      >
        {mode === 'upload' ? (
          <UploadNoteStep
            onBack={() => setMode(null)}
            onGenerated={handleGenerated}
          />
        ) : (
          <ChooseModeStep onSelect={handleModeSelected} />
        )}
      </DialogContent>
    </Dialog>
  );
};
